import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

// Generic autosave Stimulus controller for forms that should save themselves
// on every change without a Save button. Mirrors the autosave semantics of
// `form-builder-content` (debounce, single-flight, navigation-key re-debounce,
// `form-builder:autosaved` window event) without any of the form-builder
// canvas/sidebar concerns, so it can be reused on any plain form.
//
// Usage:
//   <div data-controller="form-autosave"
//        data-action="input->form-autosave#queueAutosave
//                     change->form-autosave#queueAutosave
//                     keydown->form-autosave#handleNavigationKey
//                     submit->form-autosave#handleSubmit"
//        data-form-autosave-autosave-debounce-ms-value="600">
//     <form action="..." method="post">...</form>
//   </div>
export default class extends Controller {
  static values = {
    autosaveDebounceMs: { type: Number, default: 600 },
  }

  connect() {
    this.autosaveTimeout = null
    this.autosaveAbortControllersByForm = new Map()
    this.pendingAutosaveByForm = new Map()
    this.lastAutosavePayloadByForm = new Map()
  }

  disconnect() {
    this._clearQueuedAutosave()
  }

  queueAutosave(event) {
    const form = this._formForEventTarget(event.target)
    if (!form) return

    const delay = this._autosaveDelay(event)
    this._scheduleAutosave(form, delay)
  }

  handleNavigationKey(event) {
    if (!this._isNavigationKey(event)) return
    if (!this.autosaveTimeout) return

    const form = this._formForEventTarget(event.target)
    if (!form) return

    this._scheduleAutosave(form, this.autosaveDebounceMsValue)
  }

  // Intercept submits (e.g. Enter inside a text field) so they trigger an
  // immediate autosave instead of a full page navigation.
  handleSubmit(event) {
    const form = event.target instanceof HTMLFormElement
      ? event.target
      : this._formForEventTarget(event.target)
    if (!form) return

    event.preventDefault()
    clearTimeout(this.autosaveTimeout)
    this.autosaveTimeout = null
    this._performAutosave(form)
  }

  _formForEventTarget(target) {
    if (!(target instanceof Element)) return null

    const linkedForm = target instanceof HTMLElement ? target.form : null
    if (linkedForm && this.element.contains(linkedForm)) return linkedForm

    return target.closest("form")
  }

  _autosaveDelay(event) {
    const target = event.target

    if (event.type === "input") return this.autosaveDebounceMsValue

    if (target.tagName === "SELECT") return 0
    if (target.type === "checkbox" || target.type === "radio") return 0

    // `change` on text-like inputs fires on blur (e.g. tabbing to another
    // field); keep the debounce instead of saving immediately.
    return this.autosaveDebounceMsValue
  }

  _scheduleAutosave(form, delay) {
    clearTimeout(this.autosaveTimeout)
    this.autosaveTimeout = setTimeout(() => {
      this._performAutosave(form)
    }, delay)
  }

  async _performAutosave(form) {
    if (!form.isConnected) return

    const formKey = this._formKey(form)
    const payload = this._serializeForm(form)
    if (payload === this.lastAutosavePayloadByForm.get(formKey)) return

    const existingController = this.autosaveAbortControllersByForm.get(formKey)
    if (existingController) {
      this.pendingAutosaveByForm.set(formKey, true)
      return
    }
    const abortController = new AbortController()
    this.autosaveAbortControllersByForm.set(formKey, abortController)

    const body = new FormData(form)
    body.append("autosave", "true")

    const csrfToken = document.querySelector("[name='csrf-token']")?.content

    try {
      const response = await fetch(form.action, {
        method: (form.method || "post").toUpperCase(),
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": csrfToken,
        },
        body,
        signal: abortController.signal,
      })

      const stream = await response.text()
      if (stream) Turbo.renderStreamMessage(stream)

      if (!response.ok) return

      window.dispatchEvent(new CustomEvent("form-builder:autosaved", {
        detail: { autosavedAt: new Date().toISOString() },
      }))
      this.lastAutosavePayloadByForm.set(formKey, payload)
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Autosave failed:", error)
      }
    } finally {
      if (this.autosaveAbortControllersByForm.get(formKey) === abortController) {
        this.autosaveAbortControllersByForm.delete(formKey)
      }

      if (this.pendingAutosaveByForm.get(formKey)) {
        this.pendingAutosaveByForm.delete(formKey)
        if (form.isConnected && this._formKey(form) === formKey) {
          this._performAutosave(form)
        }
      }
    }
  }

  _formKey(form) {
    return `${(form.method || "post").toUpperCase()}:${form.action}`
  }

  _serializeForm(form) {
    return new URLSearchParams(new FormData(form)).toString()
  }

  _isNavigationKey(event) {
    return [
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
      "PageUp",
      "PageDown",
    ].includes(event.key)
  }

  _clearQueuedAutosave() {
    clearTimeout(this.autosaveTimeout)
    this.autosaveTimeout = null

    this.autosaveAbortControllersByForm.forEach((controller) => controller.abort())
    this.autosaveAbortControllersByForm.clear()
    this.pendingAutosaveByForm.clear()
  }
}
